import React, { useState } from 'react';
import styles from './CanvassInfo.module.css';
import { Button } from '../layout/buttons';
import { Modal } from '../layout/Modal';

/**
 * PreambleQuestions
 * Component for viewing the preamble question and reordering its answers.
 *
 * Props:
 * - preambleQuestions: Array of preamble question objects
 * - onSave: (updatedQuestions) => Promise<void> - callback to persist changes
 * - isEditable: boolean - whether editing is allowed
 */
export default function PreambleQuestions({
  preambleQuestions = [],
  onSave,
  isEditable = true
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const openModal = () => {
    if (!isEditable || preambleQuestions.length === 0) return;
    setEditingQuestions(
      preambleQuestions.map((q) => ({
        ...q,
        answers: (q.answers || []).map((a) => ({ ...a }))
      }))
    );
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingQuestions([]);
  };

  const moveAnswerUp = (questionIndex, answerIndex) => {
    if (answerIndex <= 0) return;
    const updatedQuestions = [...editingQuestions];
    const question = updatedQuestions[questionIndex];
    const answers = [...question.answers];
    [answers[answerIndex - 1], answers[answerIndex]] = [answers[answerIndex], answers[answerIndex - 1]];
    updatedQuestions[questionIndex] = { ...question, answers };
    setEditingQuestions(updatedQuestions);
  };

  const moveAnswerDown = (questionIndex, answerIndex) => {
    const updatedQuestions = [...editingQuestions];
    const question = updatedQuestions[questionIndex];
    const answers = [...question.answers];
    if (answerIndex >= answers.length - 1) return;
    [answers[answerIndex], answers[answerIndex + 1]] = [answers[answerIndex + 1], answers[answerIndex]];
    updatedQuestions[questionIndex] = { ...question, answers };
    setEditingQuestions(updatedQuestions);
  };

  const save = async () => {
    if (!isEditable || isSaving) return;
    try {
      setIsSaving(true);
      await onSave?.(editingQuestions);
      closeModal();
    } catch (error) {
      console.error('Failed to save preamble questions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Preamble Question</div>
          {isEditable && preambleQuestions.length > 0 && (
            <a className={styles.editLink} type='button' onClick={openModal}>
              Edit order
            </a>
          )}
        </div>
        <div className={styles.inputSubLabel}>
          Pre-question to ask before the main interview
        </div>
        
        {preambleQuestions.length === 0 ? (
          <div className={styles.sectionLast} style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#6c757d',
            fontStyle: 'italic',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px dashed #dee2e6'
          }}>
            No preamble question configured
          </div>
        ) : (
          <div className={styles.sectionLast} style={{ display: 'grid', gap: '12px' }}>
            {preambleQuestions.map((pq, index) => (
              <div
                key={index}
                style={{
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fff'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#212529' }}>
                  Q{index + 1}: {pq.question_text}
                </div>
                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '8px' }}>
                  Answers ({pq.answers?.length || 0}):
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                  {(pq.answers || []).map((answer, answerIndex) => (
                    <div
                      key={answerIndex}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#495057'
                      }}
                    >
                      {String.fromCharCode(65 + answerIndex)}. {answer.label}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Modal
        open={showModal}
        onClose={closeModal}
        title={'Reorder Preamble Answers'}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
            <Button variant={'outline-neutral'} onClick={closeModal}>
              Cancel
            </Button>
            <Button variant={'primary'} disabled={!isEditable || isSaving} onClick={save}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {editingQuestions.map((pq, questionIndex) => (
            <div
              key={questionIndex}
              style={{
                padding: '12px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                marginBottom: '16px'
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                Question {questionIndex + 1}
              </div>
              <div
                style={{
                  padding: '8px',
                  backgroundColor: '#fff',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: '#212529'
                }}
              >
                {pq.question_text}
              </div>
              
              <div style={{ display: 'grid', gap: '8px' }}>
                {(pq.answers || []).map((answer, answerIndex) => (
                  <div
                    key={answerIndex}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      backgroundColor: '#fff',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px'
                    }}
                  >
                    <span style={{ fontWeight: 600, minWidth: '24px' }}>
                      {String.fromCharCode(65 + answerIndex)}.
                    </span>
                    <span style={{ flex: 1, fontSize: '14px' }}>
                      {answer.label}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6c757d', marginRight: '12px' }}>
                      {answer.anchor} ({answer.valence})
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Button
                        variant={'outline-neutral'}
                        size={'sm'}
                        onClick={() => moveAnswerUp(questionIndex, answerIndex)}
                        disabled={answerIndex === 0 || !isEditable}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        ↑
                      </Button>
                      <Button
                        variant={'outline-neutral'}
                        size={'sm'}
                        onClick={() => moveAnswerDown(questionIndex, answerIndex)}
                        disabled={answerIndex === (pq.answers || []).length - 1 || !isEditable}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        ↓
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}
